import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Grid2,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, useLocation } from 'react-router-dom'
import userService, { UserCreateInput } from '../services/userService'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { styled } from '@mui/material/styles'
import api from '../services/authService'
import PageHeader from '../components/common/PageHeader'
import DeleteIcon from '@mui/icons-material/Delete'
import InputField from '../components/common/FormFields/InputField'
import SelectField from '../components/common/FormFields/SelectField'
import { userValidationSchema } from '../validations/userValidation'

// แก้ไขการ import รูปภาพ
const defaultProfileImage = '/images/icon-picture-profile.png'

// Add styled component for upload button
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const UserAdd: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // ตรวจสอบว่ามาจากหน้า employee หรือไม่
  const isFromEmployeePage =
    new URLSearchParams(location.search).get('from') === 'employee'

  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [roles, setRoles] = useState<string[]>(
    isFromEmployeePage ? ['employee'] : ['user'],
  )
  const [isEnable, setIsEnable] = useState<boolean>(true)
  const [employeeCode, setEmployeeCode] = useState<string>('')
  const [position, setPosition] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [image, setImage] = useState<string>('')
  const [uploadLoading, setUploadLoading] = useState(false)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const handleBack = () => {
    // กลับไปหน้าที่มาจาก
    if (isFromEmployeePage) {
      navigate('/employees')
    } else {
      navigate('/users')
    }
  }

  // Add image upload handler
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    setUploadLoading(true)
    try {
      const response = await api.post<{ url: string }>(
        '/upload/file',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      setImage(response.data.url)
    } catch (error) {
      console.error('Error uploading image:', error)
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการอัพโหลดรูปภาพ',
        severity: 'error',
      })
    } finally {
      setUploadLoading(false)
    }
  }

  const validateField = async (field: string, value: any) => {
    try {
      await userValidationSchema.validateAt(field, { [field]: value })
      setFormErrors((prev) => ({ ...prev, [field]: '' }))
      return true
    } catch (error: any) {
      setFormErrors((prev) => ({ ...prev, [field]: error.message }))
      return false
    }
  }

  const handleFieldChange = async (field: string, value: any) => {
    switch (field) {
      case 'username':
        setUsername(value)
        break
      case 'password':
        setPassword(value)
        break
      case 'name':
        setName(value)
        break
      case 'phoneNumber':
        setPhoneNumber(value)
        break
      case 'email':
        setEmail(value)
        break
      case 'employeeCode':
        setEmployeeCode(value)
        break
      case 'position':
        setPosition(value)
        break
      case 'roles':
        if (isFromEmployeePage) {
          setRoles([value])
        } else {
          setRoles(typeof value === 'string' ? [value] : value)
        }
        break
      case 'isEnable':
        setIsEnable(value === 'active')
        break
    }
    await validateField(field, value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // ตรวจสอบข้อมูล
    // if (!username || !password || !name) {
    const fieldsToValidate = {
      username,
      password,
      name,
      phoneNumber,
      email,
      employeeCode,
      position,
      roles,
      isEnable: isEnable ? 'active' : 'inactive',
    }

    let isValid = true
    for (const [field, value] of Object.entries(fieldsToValidate)) {
      const fieldValid = await validateField(field, value)
      if (!fieldValid) isValid = false
    }

    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'กรุณาระบุข้อมูลที่จำเป็น',
        severity: 'error',
      })
      return
    }

    setLoading(true)

    try {
      const userData: UserCreateInput = {
        username,
        password,
        name,
        phoneNumber: phoneNumber || undefined,
        roles,
        isEnable,
        employeeCode: employeeCode || undefined,
        position: position || undefined,
        image: image || undefined,
        email: email || undefined,
      }

      await userService.createUser(userData)

      setSnackbar({
        open: true,
        message: 'เพิ่มผู้ใช้งานสำเร็จ',
        severity: 'success',
      })

      // รอให้ Snackbar แสดงสักครู่ก่อนกลับไปหน้ารายการ
      setTimeout(() => {
        if (isFromEmployeePage) {
          navigate('/employees')
        } else {
          navigate('/users')
        }
      }, 1500)
    } catch (error: any) {
      console.error('Error adding user:', error)
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || 'เกิดข้อผิดพลาดในการเพิ่มผู้ใช้งาน',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (isFromEmployeePage) {
      navigate('/employees')
    } else {
      navigate('/users')
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      <PageHeader
        title={
          isFromEmployeePage ? 'เพิ่มรายชื่อพนักงาน' : 'เพิ่มรายชื่อผู้ใช้งาน'
        }
        breadcrumb={{
          items: [
            {
              label: isFromEmployeePage ? 'รายการพนักงาน' : 'รายการผู้ใช้งาน',
              onClick: handleBack,
            },
            {
              label: isFromEmployeePage
                ? 'เพิ่มรายชื่อพนักงาน'
                : 'เพิ่มรายชื่อผู้ใช้งาน',
            },
          ],
        }}
      />

      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          p: { xs: 2, sm: 3 },
          mb: 3,
          alignItems: 'center',
          gap: 1,
          pl: 2,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
        }}
      >
        <Box component="form" onSubmit={handleSubmit} sx={{ mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {isFromEmployeePage
                ? 'เพิ่มรายชื่อพนักงาน'
                : 'เพิ่มรายชื่อผู้ใช้งาน'}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                sx={{
                  whiteSpace: 'nowrap',
                  bgcolor: '#2e7d32',
                  '&:hover': {
                    bgcolor: '#1b5e20',
                  },
                }}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                บันทึก
              </Button>
              <Button
                variant="contained"
                onClick={handleCancel}
                disabled={loading}
                sx={{
                  whiteSpace: 'nowrap',
                  bgcolor: '#A3A3A3',
                  '&:hover': {
                    bgcolor: '#8A8A8A',
                  },
                }}
              >
                ยกเลิก
              </Button>
            </Box>
          </Box>

          <Grid2 container spacing={2} direction="row">
            <Grid2 size={{ xs: 12, md: 3, lg: 2.5 }} sx={{ mr: { lg: 4 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                    mb: 1,
                    fontWeight: 500,
                    color: '#333',
                  }}
                >
                  ภาพประจำตัว
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 2,
                    bgcolor: '#f5f5f5',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={image || defaultProfileImage}
                    alt="Preview"
                    sx={{
                      width: image ? '100%' : 100,
                      height: image ? '100%' : 100,
                      objectFit: 'cover',
                    }}
                  />
                  {image && (
                    <IconButton
                      onClick={() => setImage('')}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploadLoading}
                  sx={{
                    minWidth: 'fit-content',
                    p: { xs: 0, md: 1, lg: 0 },
                  }}
                >
                  อัพโหลดรูปภาพ
                  <VisuallyHiddenInput
                    type="file"
                    onChange={handleImageUpload}
                    accept="image/*"
                  />
                </Button>
                {uploadLoading && <CircularProgress size={24} />}
              </Box>
            </Grid2>
            <Grid2 container spacing={2} size={{ xs: 12, md: 9 }}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="ชื่อผู้ใช้งาน"
                  name="username"
                  value={username}
                  onChange={(e) =>
                    handleFieldChange('username', e.target.value)
                  }
                  onBlur={(e) => validateField('username', e.target.value)}
                  error={Boolean(formErrors.username)}
                  helperText={formErrors.username}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="รหัสผ่าน"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) =>
                    handleFieldChange('password', e.target.value)
                  }
                  onBlur={(e) => validateField('password', e.target.value)}
                  error={Boolean(formErrors.password)}
                  helperText={formErrors.password}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="รหัสพนักงาน"
                  name="employeeCode"
                  value={employeeCode}
                  onChange={(e) =>
                    handleFieldChange('employeeCode', e.target.value)
                  }
                  onBlur={(e) => validateField('employeeCode', e.target.value)}
                  error={Boolean(formErrors.employeeCode)}
                  helperText={formErrors.employeeCode}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="ตำแหน่ง"
                  name="position"
                  value={position}
                  onChange={(e) =>
                    handleFieldChange('position', e.target.value)
                  }
                  onBlur={(e) => validateField('position', e.target.value)}
                  error={Boolean(formErrors.position)}
                  helperText={formErrors.position}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="ชื่อพนักงาน"
                  name="name"
                  value={name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  onBlur={(e) => validateField('name', e.target.value)}
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="เบอร์โทร"
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) =>
                    handleFieldChange('phoneNumber', e.target.value)
                  }
                  onBlur={(e) => validateField('phoneNumber', e.target.value)}
                  error={Boolean(formErrors.phoneNumber)}
                  helperText={formErrors.phoneNumber}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="อีเมล"
                  name="email"
                  value={email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  onBlur={(e) => validateField('email', e.target.value)}
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <SelectField
                    label="ประเภทผู้ใช้งาน"
                    name="roles"
                    value={isFromEmployeePage ? roles[0] : roles}
                    onChange={(e) => handleFieldChange('roles', e.target.value)}
                    onBlur={(e) => validateField('roles', e.target.value)}
                    error={Boolean(formErrors.roles)}
                    helperText={formErrors.roles}
                    multiple={!isFromEmployeePage}
                    disabled={isFromEmployeePage}
                    required
                    options={[
                      { value: 'admin', label: 'ผู้ดูแลระบบ' },
                      { value: 'user', label: 'เจ้าหน้าที่' },
                      ...(isFromEmployeePage
                        ? [{ value: 'employee', label: 'พนักงาน' }]
                        : []),
                    ]}
                  />
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 3 }}>
                <SelectField
                  label="สถานะการใช้งาน"
                  name="isEnable"
                  value={isEnable ? 'active' : 'inactive'}
                  valueColor={(value) =>
                    value === 'active' ? 'custom.green' : 'custom.red'
                  }
                  onChange={(e) =>
                    handleFieldChange('isEnable', e.target.value)
                  }
                  onBlur={(e) => validateField('isEnable', e.target.value)}
                  error={Boolean(formErrors.isEnable)}
                  helperText={formErrors.isEnable}
                  options={[
                    { value: 'active', label: 'เปิดการใช้งาน' },
                    { value: 'inactive', label: 'ปิดการใช้งาน' },
                  ]}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UserAdd
